import { useEffect, useRef } from 'react'

import { NOTIFICATION_TYPE } from '../../../../constants/common'
import {
  useDebounceFunc,
  useInlineNotifications,
  useNotification,
} from '../../../../hooks'
import {
  isDocFlowWidgetStatusCompleted,
  isDocFlowWidgetStatusSkipped,
} from '../../DocFlow.utils.widgets'
import { DOC_FLOW_MAPPING_GET_SELECTED_AREA_INFO_DEBOUNCE_TIME } from '../../DocFlowMapping'
import {
  isDocFlowUnvisitedWidget,
  isDocFlowWidgetDisabled,
} from '../../DocFlowWidgets'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowMappingContext } from '../useDocFlowMappingContext'

import {
  isAreaNotChanged,
  isNotificationPhase,
} from './useDocFlowMappingWidgetWithSelectedArea.utils'

export function useDocFlowMappingWidgetWithSelectedArea({
  apiFunction,
  areaType,
  debounceTime = DOC_FLOW_MAPPING_GET_SELECTED_AREA_INFO_DEBOUNCE_TIME,
  getSelectedAreaData,
  getSelectedAreaPayload,
  getSubmitPayload,
  hasAreaCellName,
  hasErrorCellName,
  index,
  initialNotifications,
  isAreaValue,
  phaseType,
  widgetName,
  widgetNotifications,
  onSuccessSubmitNext,
}) {
  const { docFlow, phaseDisabled } = useDocFlowContext()

  const {
    activeWidget,
    handleClickNextWidget,
    handleClickPreviousWidget,
    handleGetSelectedAreaInfo,
    handleHideSelectedArea,
    handleResetCurrentSelectedArea,
    handleToggleOpenSelectAreaInfo,
    handleUpdateFileProcess,
    isChanged,
    pdfPageCoordinates,
    processData,
    selectedBox,
    setPdfSelectAreaInfoValue,
  } = useDocFlowMappingContext()

  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveNotification,
    notifications,
  } = useInlineNotifications({
    initialNotifications,
  })

  const { handleAddErrorNotification } = useNotification()

  const isTestInProcess = useRef(false)

  const unvisited = isDocFlowUnvisitedWidget(processData, phaseType)

  const disabled =
    phaseDisabled || isDocFlowWidgetDisabled(processData, phaseType)

  function handleSubmit(values, helpers, previous) {
    return handleUpdateFileProcess(
      getSubmitPayload({
        isChanged,
        pdfPageCoordinates,
        previous,
        selectedBox,
        values,
      })
    )
  }

  function setTestInProcessNotification() {
    handlePrependNotification({
      ...widgetNotifications.testInProcess,
      type: NOTIFICATION_TYPE.warning,
    })
  }

  const widgetDone = isDocFlowWidgetStatusCompleted(processData?.[phaseType])

  const widgetSkipped = isDocFlowWidgetStatusSkipped(processData?.[phaseType])

  const hasError =
    widgetDone &&
    (processData?.[hasErrorCellName] || !processData?.[hasAreaCellName])

  function handleRemoveAdditionalNotifications() {
    for (const [key, value] of Object.entries(widgetNotifications)) {
      if (!isNotificationPhase(key)) {
        handleRemoveNotification(value.id)
      }
    }
  }

  function handleWidgetGetSelectedAreaInfo(values) {
    handleGetSelectedAreaInfo({
      apiFunction,
      data: getSelectedAreaPayload(values),
      getSelectedAreaData,
    })
      .then(data => {
        if (data.is_success) {
          handlePrependNotification({
            ...widgetNotifications.areaFound,
            type: NOTIFICATION_TYPE.success,
          })
        } else {
          handlePrependNotification({
            ...widgetNotifications.areaNotFound,
            type: NOTIFICATION_TYPE.error,
          })
        }
      })
      .catch(() => {
        handlePrependNotification({
          ...widgetNotifications.serverError,
          type: NOTIFICATION_TYPE.error,
        })
      })

    isTestInProcess.current = false

    handleRemoveNotification(widgetNotifications.testInProcess.id)

    handleRemoveNotification(widgetNotifications.phase?.id)
  }

  const { cancelDebounce, debouncedHandler } = useDebounceFunc(
    handleWidgetGetSelectedAreaInfo,
    debounceTime
  )

  function handleDebounceWidgetGetSelectedAreaInfo(...args) {
    cancelDebounce()

    isTestInProcess.current = true

    debouncedHandler(...args)
  }

  function handleWidgetGetSelectedAreaInfoAndCancelDebounce(values) {
    cancelDebounce()

    handleWidgetGetSelectedAreaInfo(values)
  }

  function handleChangeAccuracy(values) {
    cancelDebounce()

    handleRemoveAdditionalNotifications()

    handleToggleOpenSelectAreaInfo(false)

    handleWidgetGetSelectedAreaInfo(values)
  }

  function handleClickNext(values, formikHelers) {
    if (isTestInProcess.current) {
      setTestInProcessNotification()

      return
    }

    if (disabled) {
      handleResetCurrentSelectedArea()
      handleClickNextWidget(index)

      return
    }

    if (
      isAreaNotChanged({
        areaType,
        hasAreaCellName,
        isAreaValue,
        isChanged,
        processData,
        values,
      })
    ) {
      handlePrependNotification({
        ...widgetNotifications.notChanged,
        type: NOTIFICATION_TYPE.error,
      })

      return
    } else {
      handleRemoveNotification(widgetNotifications.notChanged?.id)
    }

    return handleSubmit(values, formikHelers)
      ?.then(async data => {
        await onSuccessSubmitNext?.()

        await handleResetCurrentSelectedArea()
        await handleClickNextWidget(index)

        return data
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleClickPrevious({ formikHelers, values }) {
    cancelDebounce()

    if (isTestInProcess.current) {
      setTestInProcessNotification()

      return
    }

    handleResetCurrentSelectedArea()
    handleClickPreviousWidget(index)

    if (!disabled) {
      handleSubmit(values, formikHelers, true)
    }
  }

  function handleClearFieldType() {
    isTestInProcess.current = false

    cancelDebounce()

    handleHideSelectedArea()
    handleToggleOpenSelectAreaInfo(false)
    setPdfSelectAreaInfoValue(null)

    handleRemoveNotification(widgetNotifications.phase?.id)
  }

  useEffect(() => {
    if (unvisited) {
      handleRemoveAdditionalNotifications()
    }
  }, [unvisited])

  useEffect(() => {
    if (disabled && widgetName === activeWidget) {
      if (hasError) {
        handlePrependNotification({
          ...widgetNotifications.areaNotFound,
          type: NOTIFICATION_TYPE.error,
        })
      } else if (!hasError && widgetDone && !widgetSkipped) {
        handlePrependNotification({
          ...widgetNotifications.areaFound,
          type: NOTIFICATION_TYPE.success,
        })
      }
    }
  }, [activeWidget, disabled, hasError, widgetDone, widgetSkipped])

  return {
    disabled,
    handleAddNotification,
    handleChangeAccuracy,
    handleClearFieldType,
    handleClickNext,
    handleClickPrevious,
    handleDebounceWidgetGetSelectedAreaInfo,
    handlePrependNotification,
    handleRemoveAdditionalNotifications,
    handleRemoveNotification,
    handleWidgetGetSelectedAreaInfo:
      handleWidgetGetSelectedAreaInfoAndCancelDebounce,
    hasError,
    isTestInProcess,
    nextButtonDisabled:
      (disabled || docFlow?.archived) && !widgetDone && !widgetSkipped,
    notifications,
    unvisited,
    widgetDone,
  }
}
